import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Home, FileText, Briefcase, Users, Check, ArrowRight, Star, Clock, Shield, Heart, Award, TrendingUp, Target, Lightbulb, Phone, Mail } from 'lucide-react';
import { PublicLayout } from '@/components/PublicLayout';
import { Button } from '@/components/ui/button';

const Services = () => {
  const { t, language } = useLanguage();
  const [activeService, setActiveService] = useState(0);

  const services = [
    {
      icon: Home,
      title: t.services.housing.title,
      description: t.services.housing.description,
      color: 'from-blue-500 to-blue-700',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600',
      image: '/images/apartment-interior.jpg',
      features: language === 'fr' ? [
        'Base de données exclusive de logements vérifiés',
        'Visite accompagnée des appartements',
        'Négociation des conditions avec le propriétaire',
        'Aide à la constitution du dossier locatif',
        'État des lieux d’entrée assisté',
        'Suivi post-installation'
      ] : [
        'Exclusive database of verified housing',
        'Accompanied apartment visits',
        'Negotiation of terms with landlord',
        'Help preparing rental application',
        'Assisted move-in inspection',
        'Post-installation follow-up'
      ],
      stats: [
        { number: '200+', label: language === 'fr' ? 'Logements partenaires' : 'Partner housing' },
        { number: '2 sem', label: language === 'fr' ? 'Délai moyen' : 'Average delay' },
        { number: '95%', label: language === 'fr' ? 'Taux de réussite' : 'Success rate' }
      ]
    },
    {
      icon: FileText,
      title: t.services.admin.title,
      description: t.services.admin.description,
      color: 'from-purple-500 to-purple-700',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600',
      image: '/images/office-work.jpg',
      features: language === 'fr' ? [
        'Aide complète dossier CAF (demande + suivi)',
        'Inscription sécurité sociale étudiante',
        'Accompagnement préfecture (titre de séjour)',
        'Ouverture compte bancaire facilitée',
        'Souscription assurance habitation',
        'Sessions d’information collectives'
      ] : [
        'Complete CAF file assistance (application + follow-up)',
        'Student social security registration',
        'Prefecture support (residence permit)',
        'Facilitated bank account opening',
        'Home insurance subscription',
        'Collective information sessions'
      ],
      stats: [
        { number: '15+', label: language === 'fr' ? 'Démarches traitées' : 'Procedures handled' },
        { number: '100%', label: language === 'fr' ? 'Dossiers validés' : 'Files validated' },
        { number: '1 mois', label: language === 'fr' ? 'Délai CAF moyen' : 'Average CAF delay' }
      ]
    },
    {
      icon: Briefcase,
      title: t.services.job.title,
      description: t.services.job.description,
      color: 'from-green-500 to-green-700',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600',
      image: '/images/team-meeting.jpg',
      features: language === 'fr' ? [
        'Ateliers CV & lettre de motivation en français',
        'Simulation d’entretiens d’embauche',
        'Réseau d’entreprises partenaires',
        'Offres d’emploi étudiant exclusives',
        'Conseils sur la législation du travail',
        'Suivi personnalisé dans vos recherches'
      ] : [
        'CV & cover letter workshops in French',
        'Job interview simulations',
        'Network of partner companies',
        'Exclusive student job offers',
        'Advice on labor legislation',
        'Personalized follow-up in your search'
      ],
      stats: [
        { number: '80%', label: language === 'fr' ? 'Trouvent un job' : 'Find a job' },
        { number: '50+', label: language === 'fr' ? 'Entreprises partenaires' : 'Partner companies' },
        { number: '1 mois', label: language === 'fr' ? 'Délai moyen' : 'Average delay' }
      ]
    },
    {
      icon: Users,
      title: t.services.integration.title,
      description: t.services.integration.description,
      color: 'from-orange-500 to-orange-700',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-600',
      image: '/images/friends-group.jpg',
      features: language === 'fr' ? [
        'Événements de bienvenue mensuels',
        'Activités culturelles et sportives',
        'Parrainage par anciens étudiants',
        'Groupes WhatsApp par nationalité',
        'Visites guidées de Lyon',
        'Cours de français conversationnel'
      ] : [
        'Monthly welcome events',
        'Cultural and sports activities',
        'Mentoring by former students',
        'WhatsApp groups by nationality',
        'Guided tours of Lyon',
        'Conversational French classes'
      ],
      stats: [
        { number: '30+', label: language === 'fr' ? 'Événements/an' : 'Events/year' },
        { number: '40+', label: language === 'fr' ? 'Nationalités' : 'Nationalities' },
        { number: '500+', label: language === 'fr' ? 'Membres actifs' : 'Active members' }
      ]
    },
  ];

  const processSteps = [
    {
      number: '1',
      title: language === 'fr' ? 'Premier contact' : 'First contact',
      description: language === 'fr' 
        ? 'Contactez-nous par email, téléphone ou venez nous rencontrer à notre bureau.'
        : 'Contact us by email, phone or visit our office.',
      icon: Phone
    },
    {
      number: '2',
      title: language === 'fr' ? 'Rendez-vous personnalisé' : 'Personalized appointment',
      description: language === 'fr'
        ? 'Un membre de notre équipe évalue vos besoins et vous propose un plan d’action.'
        : 'A team member assesses your needs and proposes an action plan.',
      icon: Users
    },
    {
      number: '3',
      title: language === 'fr' ? 'Action concrète' : 'Concrete action',
      description: language === 'fr'
        ? 'Nous vous accompagnons étape par étape dans vos démarches.'
        : 'We support you step by step in your procedures.',
      icon: Target
    },
    {
      number: '4',
      title: language === 'fr' ? 'Suivi continu' : 'Continuous follow-up',
      description: language === 'fr'
        ? 'Notre accompagnement continue jusqu’à votre installation complète et au-delà.'
        : 'Our support continues until your complete settlement and beyond.',
      icon: Heart
    }
  ];

  const testimonials = [
    {
      name: 'Sabine N\'GO',
      country: language === 'fr' ? 'Cameroun' : 'Cameroun',
      service: language === 'fr' ? 'Logement' : 'Housing',
      text: language === 'fr'
        ? 'J’ai trouvé mon T1 en 10 jours grâce à AILE ! L’équipe m’a accompagnée à chaque visite et a négocié le loyer. Service exceptionnel.'
        : 'I found my 1-bedroom in 10 days thanks to AILE! The team accompanied me to each visit and negotiated the rent. Exceptional service.',
      rating: 5
    },
    {
      name: 'Aminata DIOP',
      country: language === 'fr' ? 'Sénégal' : 'Sénégal',
      service: language === 'fr' ? 'Démarches admin' : 'Admin procedures',
      text: language === 'fr'
        ? 'Les démarches CAF et préfecture sont un cauchemar quand on ne parle pas bien français. AILE m’a tout expliqué et m’a aidé à tout remplir. Merci !'
        : 'CAF and prefecture procedures are a nightmare when you don\'t speak French well. AILE explained everything and helped me fill everything. Thank you!',
      rating: 5
    },
    {
      name: 'Amina Hassan',
      country: language === 'fr' ? 'Tunisie' : 'Tunisia',
      service: language === 'fr' ? 'Job étudiant' : 'Student job',
      text: language === 'fr'
        ? 'Grâce aux ateliers CV et au réseau d’AILE, j’ai décroché un job à mi-temps dans un café. Parfait pour financer mes études !'
        : 'Thanks to AILE\'s CV workshops and network, I got a part-time job in a cafe. Perfect to finance my studies!',
      rating: 5
    }
  ];

  const whyChooseUs = [
    {
      icon: Award,
      title: language === 'fr' ? 'Expertise' : 'Expertise',
      description: language === 'fr' ? '+5 ans d’expérience' : '+5 years experience'
    },
    {
      icon: Shield,
      title: language === 'fr' ? 'Sécurité' : 'Security',
      description: language === 'fr' ? 'Partenaires vérifiés' : 'Verified partners'
    },
    {
      icon: Clock,
      title: language === 'fr' ? 'Rapidité' : 'Speed',
      description: language === 'fr' ? 'Réponse < 24h' : 'Response < 24h'
    },
    {
      icon: Heart,
      title: language === 'fr' ? 'Bienveillance' : 'Kindness',
      description: language === 'fr' ? 'Équipe dédiée' : 'Dedicated team'
    }
  ];

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-white via-primary-50/30 to-white">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-6 px-4 py-2 bg-primary-100 text-[#127abb] rounded-full text-sm font-semibold">
              ✨ {language === 'fr' ? '4 Services Complémentaires' : '4 Complementary Services'}
            </div>
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight text-slate-900 mb-6">
              {t.services.title}
            </h1>
            <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed">
              {t.services.subtitle}
            </p>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto mb-10">
              {language === 'fr'
                ? 'De la recherche de logement à l’intégration complète, nous vous accompagnons à chaque étape de votre installation à Lyon.'
                : 'From housing search to complete integration, we support you at every step of your settlement in Lyon.'}
            </p>
            <Link to="/contact">
              <Button className="bg-[#127abb] hover:bg-[#0e6292] text-white rounded-full px-10 py-6 text-lg shadow-xl">
                {language === 'fr' ? 'Prendre rendez-vous' : 'Make an appointment'}
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <button
                  key={index}
                  onClick={() => setActiveService(index)}
                  className={`text-left p-6 rounded-2xl border-2 transition-all duration-300 ${
                    activeService === index
                      ? 'border-[#127abb] bg-primary-50 shadow-xl scale-105'
                      : 'border-slate-200 hover:border-[#127abb]/50 hover:shadow-lg'
                  }`}
                >
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-4 ${
                    activeService === index ? 'bg-[#127abb]' : service.bgColor
                  }`}>
                    <Icon className={activeService === index ? 'text-white' : service.textColor} size={28} strokeWidth={1.5} />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{service.title}</h3>
                  <p className="text-sm text-slate-600">{service.description.substring(0, 60)}...</p>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Active Service Detail */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className={`inline-block px-4 py-2 ${services[activeService].bgColor} ${services[activeService].textColor} rounded-full text-sm font-semibold mb-4`}>
                Service {activeService + 1}/4
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                {services[activeService].title}
              </h2>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                {services[activeService].description}
              </p>

              {/* Features List */}
              <div className="space-y-4 mb-8">
                {services[activeService].features.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-[#127abb] rounded-full flex items-center justify-center mt-0.5">
                      <Check className="text-white" size={14} strokeWidth={3} />
                    </div>
                    <p className="text-slate-700 leading-relaxed">{feature}</p>
                  </div>
                ))}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 pt-6 border-t border-slate-200">
                {services[activeService].stats.map((stat, idx) => (
                  <div key={idx} className="text-center">
                    <div className="text-3xl font-bold text-[#127abb] mb-1">{stat.number}</div>
                    <div className="text-xs text-slate-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <img
                src={services[activeService].image}
                alt={services[activeService].title}
                className="rounded-3xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* All Services Grid */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              {language === 'fr' ? 'Vue d’ensemble de nos services' : 'Overview of our services'}
            </h2>
            <p className="text-xl text-slate-600">
              {language === 'fr' 
                ? 'Un accompagnement complet pour votre réussite à Lyon'
                : 'Complete support for your success in Lyon'}
            </p>
          </div>

          <div className="space-y-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              const isEven = index % 2 === 0;
              return (
                <div
                  key={index}
                  className={`flex flex-col ${
                    isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  } gap-8 items-center bg-slate-50 rounded-3xl p-8 hover:shadow-xl transition-shadow`}
                >
                  <div className="lg:w-1/3">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="rounded-2xl shadow-lg w-full h-64 object-cover"
                    />
                  </div>
                  <div className="lg:w-2/3">
                    <div className="flex items-center gap-4 mb-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${service.bgColor}`}>
                        <Icon className={service.textColor} size={24} strokeWidth={1.5} />
                      </div>
                      <h3 className="text-2xl font-bold text-slate-900">{service.title}</h3>
                    </div>
                    <p className="text-slate-600 mb-6 leading-relaxed">{service.description}</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {service.features.slice(0, 6).map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <Check className="text-[#127abb] flex-shrink-0" size={16} strokeWidth={3} />
                          <span className="text-sm text-slate-700">{feature.substring(0, 30)}{feature.length > 30 ? '...' : ''}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-primary-50 to-blue-50">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              {language === 'fr' ? 'Notre processus d’accompagnement' : 'Our support process'}
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              {language === 'fr'
                ? 'Une méthode prouvée pour garantir votre succès'
                : 'A proven method to guarantee your success'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="relative">
                  {index < processSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-12 left-[60%] w-full h-0.5 bg-[#127abb]/30 z-0"></div>
                  )}
                  <div className="relative bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2 z-10">
                    <div className="absolute -top-4 -left-4 w-12 h-12 bg-[#127abb] text-white rounded-full flex items-center justify-center text-xl font-bold shadow-lg">
                      {step.number}
                    </div>
                    <div className="w-14 h-14 bg-primary-50 rounded-xl flex items-center justify-center mb-4 mx-auto">
                      <Icon className="text-[#127abb]" size={28} />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-3 text-center">{step.title}</h3>
                    <p className="text-sm text-slate-600 text-center leading-relaxed">{step.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              {language === 'fr' ? 'Pourquoi AILE ?' : 'Why AILE?'}
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="text-center p-6 bg-slate-50 rounded-2xl hover:bg-white hover:shadow-lg transition-all">
                  <div className="w-16 h-16 bg-[#127abb] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="text-white" size={28} />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-600">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              {language === 'fr' ? 'Témoignages' : 'Testimonials'}
            </h2>
            <p className="text-xl text-slate-600">
              {language === 'fr' ? 'Ce que disent nos étudiants' : 'What our students say'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="text-amber-400 fill-amber-400" size={18} />
                  ))}
                </div>
                <p className="text-slate-700 mb-6 italic leading-relaxed">"{testimonial.text}"</p>
                <div className="border-t border-slate-200 pt-4">
                  <p className="font-bold text-slate-900">{testimonial.name}</p>
                  <p className="text-sm text-slate-600">{testimonial.country} • {testimonial.service}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-[#127abb] to-[#0e6292] text-white">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              {language === 'fr' ? 'Un tarif unique et transparent' : 'A unique and transparent price'}
            </h2>
            <div className="text-7xl font-bold mb-4">20€</div>
            <p className="text-2xl text-primary-100 mb-8">
              {language === 'fr' ? 'Paiement unique - Accès à vie' : 'One-time payment - Lifetime access'}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10 text-left">
              {[
                language === 'fr' ? 'Accès à TOUS nos services' : 'Access to ALL our services',
                language === 'fr' ? 'Accompagnement personnalisé' : 'Personalized support',
                language === 'fr' ? 'Base de données logements' : 'Housing database',
                language === 'fr' ? 'Événements et activités' : 'Events and activities',
                language === 'fr' ? 'Ateliers et formations' : 'Workshops and training',
                language === 'fr' ? 'Suivi jusqu’à fin études' : 'Support until end of studies'
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <Check className="flex-shrink-0" size={24} strokeWidth={3} />
                  <span className="text-lg">{item}</span>
                </div>
              ))}
            </div>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[#127abb] hover:bg-slate-100 rounded-full px-12 py-7 text-xl shadow-2xl">
                {language === 'fr' ? 'Devenir membre maintenant' : 'Become a member now'}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              {language === 'fr' ? 'Questions fréquentes' : 'FAQ'}
            </h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            <details className="group bg-slate-50 hover:bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-lg transition-all">
              <summary className="flex items-center justify-between cursor-pointer list-none">
                <h3 className="text-lg font-semibold text-slate-900">
                  {language === 'fr' ? 'Puis-je choisir seulement certains services ?' : 'Can I choose only certain services?'}
                </h3>
                <ArrowRight className="text-[#127abb] group-open:rotate-90 transition-transform" size={20} />
              </summary>
              <p className="mt-4 text-slate-600">
                {language === 'fr'
                  ? 'L’adhésion de 20€ vous donne accès à TOUS nos services sans restriction. Vous pouvez utiliser uniquement ceux dont vous avez besoin, quand vous en avez besoin.'
                  : 'The €20 membership gives you access to ALL our services without restriction. You can use only those you need, when you need them.'}
              </p>
            </details>

            <details className="group bg-slate-50 hover:bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-lg transition-all">
              <summary className="flex items-center justify-between cursor-pointer list-none">
                <h3 className="text-lg font-semibold text-slate-900">
                  {language === 'fr' ? 'Combien de temps dure l’accompagnement ?' : 'How long does the support last?'}
                </h3>
                <ArrowRight className="text-[#127abb] group-open:rotate-90 transition-transform" size={20} />
              </summary>
              <p className="mt-4 text-slate-600">
                {language === 'fr'
                  ? 'Notre accompagnement est valable pendant toute la durée de vos études à Lyon. Vous pouvez nous solliciter à tout moment pour de l’aide ou des conseils.'
                  : 'Our support is valid throughout your studies in Lyon. You can contact us at any time for help or advice.'}
              </p>
            </details>

            <details className="group bg-slate-50 hover:bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-lg transition-all">
              <summary className="flex items-center justify-between cursor-pointer list-none">
                <h3 className="text-lg font-semibold text-slate-900">
                  {language === 'fr' ? 'Où se trouve votre bureau ?' : 'Where is your office located?'}
                </h3>
                <ArrowRight className="text-[#127abb] group-open:rotate-90 transition-transform" size={20} />
              </summary>
              <p className="mt-4 text-slate-600">
                {language === 'fr'
                  ? 'Notre bureau est situé au 1 place Abbé Pierre, 69009 Lyon. Nous sommes ouverts du lundi au vendredi de 9h à 19h, et sur rendez-vous le samedi.'
                  : 'Our office is located at 1 place Abbé Pierre, 69009 Lyon. We are open Monday to Friday from 9am to 7pm, and by appointment on Saturday.'}
              </p>
            </details>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            {language === 'fr' ? 'Prêt à démarrer ?' : 'Ready to start?'}
          </h2>
          <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
            {language === 'fr'
              ? 'Rejoignez AILE aujourd’hui et bénéficiez d’un accompagnement complet pour votre réussite à Lyon'
              : 'Join AILE today and benefit from complete support for your success in Lyon'}
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-[#127abb] hover:bg-[#0e6292] text-white rounded-full px-10 py-7 text-lg">
                {language === 'fr' ? 'Nous contacter' : 'Contact us'}
                <Mail className="ml-2" size={20} />
              </Button>
            </Link>
            <Link to="/listings">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 rounded-full px-10 py-7 text-lg">
                {language === 'fr' ? 'Voir les logements' : 'View housing'}
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default Services;